import { API } from '../Api';

// Hội viên kênh: viewer trả Ca (hoặc dùng lượt Cab+) để thành hội viên của một creator.
// Creator cài ở Cài đặt Donate; overlay OBS đọc qua khoá riêng.

const BASE = '/v1/payment-service/membership';
const PUBLIC = '/v1/payment-service/public/membership';

export interface MembershipStats {
  activeMembers: number;
  activeViaCabPlus: number;
  joinsThisMonth: number;
  earnedThisMonthCa: number;
  pendingCa: number;
}

export interface MyMembership {
  isEnabled: boolean;
  priceCa: number;
  goalTarget: number;
  effectDiscountPercent: number;
  showRecentTicker: boolean;
  overlayKey: string | null;
  overlayOnline: boolean;
  priceOptions: number[];
  /** Phần trăm giá gốc streamer nhận; máy chủ cũ chưa trả thì giao diện dùng 70. */
  creatorSharePercent?: number;
  stats: MembershipStats;
}

export interface SaveMembershipBody {
  isEnabled: boolean;
  priceCa: number;
  goalTarget: number;
  effectDiscountPercent: number;
  showRecentTicker: boolean;
}

export interface MemberItem {
  userId: string;
  name: string;
  expiresAt: string;
  lastSource: string;
}

export interface MembersPage {
  items: MemberItem[];
  total: number;
}

export interface MembershipStatus {
  enabled: boolean;
  priceCa: number;
  payPriceCa: number;
  isPremium: boolean;
  discountPercent: number;
  isMember: boolean;
  expiresAt: string | null;
  slot: {
    available: boolean;
    validTo: string | null;
    usedForCreatorName: string | null;
  };
  topUpBalance: number;
  memberCount: number;
}

export interface PublicMembership {
  enabled: boolean;
  priceCa: number;
  memberCount: number;
  perks: string[];
}

export interface CabPlusSlot {
  isPremium: boolean;
  current: {
    validFrom: string;
    validTo: string;
    used: boolean;
    usedForCreatorName: string | null;
  } | null;
}

export interface MembershipEvent {
  joinId: string;
  viewerName: string;
  viaCabPlus: boolean;
  totalJoins: number;
  memberCount: number;
}

export interface MembershipOverlay {
  goalTarget: number;
  memberCount: number;
  showRecentTicker: boolean;
  recentNames: string[];
  queued: MembershipEvent[];
}

export const getMyMembership = (): Promise<MyMembership> =>
  API.get(`${BASE}/me`).then((r) => r.data);

export const saveMembership = (body: SaveMembershipBody): Promise<MyMembership> =>
  API.post(`${BASE}/me`, body).then((r) => r.data);

export const regenerateMembershipKey = (): Promise<{ overlayKey: string }> =>
  API.post(`${BASE}/me/regenerate-key`).then((r) => r.data);

export const getMyMembers = (page: number): Promise<MembersPage> =>
  API.get(`${BASE}/me/members`, { params: { page } }).then((r) => r.data);

export const getMembershipStatus = (creatorId: string): Promise<MembershipStatus> =>
  API.get(`${BASE}/status`, { params: { creatorId } }).then((r) => r.data);

export const joinMembership = (
  creatorId: string,
  expectedPayCa: number,
): Promise<MembershipStatus> =>
  API.post(`${BASE}/join`, { creatorId, expectedPayCa }).then((r) => r.data);

export const joinMembershipWithCabPlus = (
  creatorId: string,
): Promise<MembershipStatus> =>
  API.post(`${BASE}/join-with-cabplus`, { creatorId }).then((r) => r.data);

export const getCabPlusSlot = (): Promise<CabPlusSlot> =>
  API.get(`${BASE}/cabplus-slot`).then((r) => r.data);

export const getPublicMembership = (userId: string): Promise<PublicMembership> =>
  API.get(PUBLIC, { params: { userId } }).then((r) => r.data);

/** Tra gom lô: trả về các userId đang là hội viên còn hạn của creator (tối đa 100 id mỗi lần). */
export const getActiveMembers = (
  creatorId: string,
  userIds: string[],
): Promise<string[]> =>
  API.post<{ userIds: string[] }>(`${PUBLIC}/members`, { creatorId, userIds }).then(
    (r) => r.data?.userIds ?? [],
  );

export const getMembershipOverlay = (
  userId: string,
  key: string,
): Promise<MembershipOverlay> =>
  API.get(`${PUBLIC}/overlay`, { params: { userId, key } }).then((r) => r.data);

export const pingMembershipOverlay = (userId: string, key: string): Promise<void> =>
  API.post(`${PUBLIC}/overlay/ping`, null, { params: { userId, key } }).then(
    (r) => r.data,
  );

export const markMembershipPlayed = (
  userId: string,
  key: string,
  joinId: string,
): Promise<void> =>
  API.post(`${PUBLIC}/overlay/played`, { joinId }, { params: { userId, key } }).then(
    (r) => r.data,
  );
